import React from 'react'

import Box from 'components_new/atoms/Box'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SectionHeader from 'components_new/molecules/SectionHeader'

import { OrderWarning } from 'redux/selectors/internal/DataRuns'
import { formatClock } from '../utils'

export interface DataRunsOrderWarningsProps {
  onSelectJobByName: (jobName: string, customerId?: string) => void
  orderWarnings: OrderWarning[]
}

function warningText(warning: OrderWarning): string {
  const jobTime = formatClock(
    warning.job_completed_at ?? warning.job_started_at
  )
  const simTime = formatClock(warning.sim_started_at)

  if (warning.kind === 'delivery_after') {
    return `${warning.job_name} landade ${formatClock(
      warning.job_started_at
    )} efter att ${
      warning.sim_job_name
    } startade ${simTime} — SIM kan ha läst gammal data.`
  }

  if (warning.kind === 'started_after') {
    return `${warning.job_name} startade ${formatClock(
      warning.job_started_at
    )} efter att ${
      warning.sim_job_name
    } startade ${simTime} — SIM kan ha läst gammal data.`
  }

  return `${warning.job_name} var inte klart förrän ${jobTime} men ${warning.sim_job_name} startade ${simTime} — SIM kan ha läst gammal data.`
}

/**
 * `deviation_minutes` means different things per kind (see the wire
 * contract), so the label has to say which - "överlapp" on a job that
 * started two hours after the SIM describes nothing that happened.
 */
function deviationText(warning: OrderWarning): string {
  if (warning.kind === 'delivery_after') {
    return `landade ${warning.deviation_minutes} min efter SIM`
  }

  if (warning.kind === 'started_after') {
    return `startade ${warning.deviation_minutes} min efter SIM`
  }

  return `${warning.deviation_minutes} min överlapp`
}

/**
 * "Ordningsvarningar" - jobs that finished (or started), and file
 * deliveries that landed, too close to when the dependent SIM run
 * started. Rows have no `JobSummary` handle of their own (a warning is a
 * pairing, not a run), so clicks go through `onSelectJobByName` with the
 * source job's name.
 */
const DataRunsOrderWarnings = (props: DataRunsOrderWarningsProps) => {
  const { onSelectJobByName, orderWarnings } = props

  return (
    <Box
      sx={{
        bgcolor: 'error.transparent',
        border: '1px solid',
        borderColor: 'error.light',
        borderRadius: '20px',
        p: '20px 22px'
      }}
    >
      <SectionHeader
        body="Jobb som startade innan sitt beroende (jobb eller fil) var klart"
        title="Ordningsvarningar"
      />

      {orderWarnings.length === 0 ? (
        <EmptyPlaceholder title="Inga ordningsvarningar." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
          {orderWarnings.map((warning, index) => (
            <Box
              key={`${warning.customer.id}-${warning.job_name}-${index}`}
              onClick={() =>
                onSelectJobByName(warning.job_name, warning.customer.id)
              }
              sx={{
                alignItems: 'center',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'error.light',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                gap: 1.5,
                p: '12px 16px',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <Tag
                color="error"
                label={
                  warning.kind === 'delivery_after' ? 'Leverans' : 'Ordning'
                }
              />
              <Text sx={{ fontWeight: 600, minWidth: '120px' }} variant="body2">
                {warning.customer.name}
              </Text>
              <Text sx={{ flex: 1, minWidth: 0 }} variant="body2">
                {warningText(warning)}
              </Text>
              <Text
                sx={{
                  color: 'error.text',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}
                variant="body2"
              >
                {deviationText(warning)}
              </Text>
            </Box>
          ))}
        </Box>
      )}

      <Text
        component="div"
        sx={{ color: 'text.disabled', mt: 1.5 }}
        variant="caption"
      >
        Beroenden läses från jobbens ordning i Glue-workflow och Homepals
        schema.
      </Text>
    </Box>
  )
}

DataRunsOrderWarnings.displayName = 'DataRunsOrderWarnings'
export default DataRunsOrderWarnings
